import React, { useState } from 'react';
import { Modal, View, Text, TextInput, TouchableOpacity, StyleSheet, Alert, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { colores } from '../../../../styles/variablesCss';
import { BACKEND_URL } from '../../../../Config/Const';
import VerificationCodeModal from './VerificationCodeModal';
import { trackCreateAccountClick } from '../../../../utils/analytics';

const CreateAccount = ({ visible, onClose }) => {
  const navigation = useNavigation();
  const [formData, setFormData] = useState({
    mail: '',
    documento: '',
    cont: '',
    confirmCont: ''
  });
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.mail || !formData.documento || !formData.cont || !formData.confirmCont) {
      setError('Por favor complete todos los campos');
      return;
    }

    if (formData.cont !== formData.confirmCont) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (!aceptaTerminos) {
      setError('Debe aceptar los términos y condiciones');
      return;
    }

    const documentoNumero = parseInt(formData.documento, 10);
    if (Number.isNaN(documentoNumero)) {
      setError('El DNI debe ser un número válido');
      return;
    }

    setIsLoading(true);
    try {
      const body = JSON.stringify({
        "request": {
          mail: formData.mail,
          doc: documentoNumero,
          cont: formData.cont,
          cont2: formData.confirmCont
        }
      });

      const response = await fetch(`${BACKEND_URL}/rest/usuarios/usuario`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body
      });

      const data = await response.json();

      if (response.ok && data.dsResultado?.resultado?.[0]?.resultado) {
        trackCreateAccountClick({ email: formData.mail });
        setShowVerification(true);
      } else {
        setError(data.dsResultado?.resultado?.[0]?.mensaje || 'Error al crear la cuenta');
      }
    } catch (error) {
      console.error('Error al crear cuenta:', error);
      setError('Error de conexión');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationComplete = (success) => {
    setShowVerification(false);
    if (success) {
      onClose();
    }
  };

  const handleTermsPress = () => {
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') {
        window.open('/terminos-y-condiciones', '_blank', 'noopener,noreferrer');
      }
      return;
    }
    navigation.navigate('TerminosYCondiciones');
  };

  if (showVerification) {
    return <VerificationCodeModal visible={true} onClose={handleVerificationComplete} mail={formData.mail} />;
  }

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={onClose}
    >

      <View style={styles.modalOverlay}>

        <View style={styles.modalContent}>

          <TouchableOpacity
            style={styles.closeButton}
            onPress={onClose}
            hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          >
            <Text style={styles.closeButtonText}>×</Text>
          </TouchableOpacity>
          <View style={styles.contentRow}>
            {/* Columna izquierda: textos */}
            <View style={styles.leftColumn}>
              <Text style={styles.heroTitle}>Creá tu cuenta</Text>
              <Text style={styles.heroSubtitle}>y comprá desde donde quieras.</Text>
            </View>

            {/* Columna derecha: formulario */}
            <View style={styles.rightColumn}>
              <TextInput
                style={styles.inputPill}
                placeholder="Correo electrónico"
                value={formData.mail}
                onChangeText={(text) => handleChange('mail', text)}
                keyboardType="email-address"
                autoCapitalize="none"
                placeholderTextColor="#A0A0A0"
              />
              <TextInput
                style={styles.inputPill}
                placeholder="DNI"
                value={formData.documento}
                onChangeText={(text) => handleChange('documento', text)}
                keyboardType="numeric"
                placeholderTextColor="#A0A0A0"
              />
              <TextInput
                style={styles.inputPill}
                placeholder="Contraseña"
                value={formData.cont}
                onChangeText={(text) => handleChange('cont', text)}
                secureTextEntry
                placeholderTextColor="#A0A0A0"
              />
              <TextInput
                style={styles.inputPill}
                placeholder="Confirmar Contraseña"
                value={formData.confirmCont}
                onChangeText={(text) => handleChange('confirmCont', text)}
                secureTextEntry
                placeholderTextColor="#A0A0A0"
              />

              <View style={styles.termsBlock}>
                <TouchableOpacity
                  style={styles.checkbox}
                  onPress={() => setAceptaTerminos(!aceptaTerminos)}
                  activeOpacity={0.8}
                >
                  {aceptaTerminos && <Text style={styles.checkmark}>✓</Text>}
                </TouchableOpacity>
                <Text style={styles.termsText}>
                  Acepto{' '}
                  <Text style={styles.termsLink} onPress={handleTermsPress}>
                    Términos y Condiciones
                  </Text>
                </Text>
              </View>

              {error ? <Text style={styles.errorText}>{error}</Text> : null}

              <View style={styles.actionsRow}>
                <View style={{ flex: 1 }} />
                <TouchableOpacity
                  style={styles.primaryButton}
                  onPress={handleSubmit}
                  disabled={isLoading}
                  className="lead"
                >
                  <Text style={styles.primaryButtonText}>{isLoading ? 'Creando cuenta...' : 'Crear mi cuenta'}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: colores.blanco,
    borderRadius: 10,
    width: '94%',
    maxWidth: 900,
    padding: 24,
  },
  contentRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
  },
  leftColumn: {
    flex: 1,
    paddingRight: 10,
  },
  rightColumn: {
    flex: 1,
    paddingLeft: 10,
    alignItems: 'stretch',
  },
  closeButton: {
    position: 'absolute',
    right: -5,
    top: -18,
    padding: 10,
    zIndex: 10,
    cursor: 'pointer',
  },
  closeButtonText: {
    fontSize: 30,
    color: colores.rojo || '#E31837',
    fontWeight: 'bold',
  },
  heroTitle: {
    color: '#E31837',
    fontWeight: '800',
    fontSize: 28,
    marginBottom: 4,
  },
  heroSubtitle: {
    color: '#333',
    fontSize: 18,
    marginBottom: 18,
  },
  heroNote: {
    color: '#8A8A8A',
    fontSize: 12,
  },
  inputPill: {
    height: 42,
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderRadius: 21,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    marginBottom: 10,
  },
  termsBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 6,
    marginBottom: 6,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 1,
    borderColor: '#ccc',
    marginRight: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkmark: {
    color: '#E31837',
    fontSize: 16,
  },
  termsText: {
    color: '#666',
  },
  termsLink: {
    color: '#E31837',
    textDecorationLine: 'underline',
    cursor: 'pointer',
  },
  actionsRow: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  primaryButton: {
    backgroundColor: '#E31837',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 14,
  },
  errorText: {
    color: '#E31837',
    marginBottom: 10,
    textAlign: 'center',
  }
});

export default CreateAccount;
